import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const API = process.env.REACT_APP_BACKEND_URL;

// Slugify a string for heading ids
const slugify = (str) =>
  String(str || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\u0900-\u097F\s-]/g, "") // keep word chars, devanagari, spaces, dashes
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .substring(0, 60) || "section";

// Parse blog HTML: assign ids to H2/H3, return updated HTML + TOC items
const buildTocAndHtml = (rawHtml) => {
  if (!rawHtml || typeof window === "undefined") {
    return { html: rawHtml || "", toc: [], words: 0 };
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = rawHtml;
  const headings = tmp.querySelectorAll("h2, h3");
  const slugCount = {};
  const toc = [];
  headings.forEach((h) => {
    const text = (h.textContent || "").trim();
    if (!text) return;
    let base = slugify(text);
    slugCount[base] = (slugCount[base] || 0) + 1;
    const id = slugCount[base] > 1 ? `${base}-${slugCount[base]}` : base;
    h.setAttribute("id", id);
    toc.push({ id, text, level: h.tagName.toLowerCase() });
  });
  const words = (tmp.textContent || "").trim().split(/\s+/).filter(Boolean).length;
  return { html: tmp.innerHTML, toc, words };
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [tocOpen, setTocOpen] = useState(false);
  const [related, setRelated] = useState([]);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  const WHATSAPP_NUMBER = "917633997555";
  const WHATSAPP_MSG = "Hi Amatya, I would like to get my kundli's detailed analysis. Please guide me.";
  const whatsappCtaUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`;

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await fetch(`${API}/api/blogs/${slug}`);
        if (!res.ok) { setNotFound(true); setLoading(false); return; }
        const data = await res.json();
        setBlog(data);
      } catch (e) {
        setNotFound(true);
      }
      setLoading(false);
    };
    fetchBlog();
  }, [slug]);

  // Instagram embed loader
  useEffect(() => {
    if (blog && blog.content && blog.content.includes("instagram-media")) {
      if (!document.querySelector('script[src*="instagram.com/embed.js"]')) {
        const s = document.createElement("script");
        s.src = "https://www.instagram.com/embed.js";
        s.async = true;
        document.body.appendChild(s);
      } else if (window.instgrm) { window.instgrm.Embeds.process(); }
    }
  }, [blog]);

  // Fetch related posts (auto-match by shared tags)
  useEffect(() => {
    if (!blog) return;
    const fetchRelated = async () => {
      try {
        const res = await fetch(`${API}/api/blogs?sort=newest`);
        const data = await res.json();
        const all = (data.blogs || []).filter(b => b.slug !== blog.slug);
        const myTags = new Set((blog.tags || []).map(t => String(t).toLowerCase()));
        const scored = all.map(b => {
          const overlap = (b.tags || []).filter(t => myTags.has(String(t).toLowerCase())).length;
          return { ...b, _score: overlap };
        });
        const matched = scored.filter(b => b._score > 0).sort((a, b) => b._score - a._score);
        // If we don't have 3 tag-matches, top up with newest other blogs
        const fillers = scored.filter(b => b._score === 0);
        const final = [...matched, ...fillers].slice(0, 3);
        setRelated(final);
      } catch (e) { /* silent */ }
    };
    fetchRelated();
  }, [blog]);

  // Compute TOC + reading time
  const { html: processedHtml, toc, words } = useMemo(
    () => buildTocAndHtml(blog?.content || ""),
    [blog?.content]
  );
  const readingTime = Math.max(1, Math.round(words / 200));

  // Highlight active TOC item on scroll
  useEffect(() => {
    if (!toc.length || !contentRef.current) return;
    const headingEls = toc.map(t => document.getElementById(t.id)).filter(Boolean);
    if (!headingEls.length) return;
    const onScroll = () => {
      const offset = 120;
      let current = headingEls[0]?.id || "";
      for (const el of headingEls) {
        if (el.getBoundingClientRect().top - offset <= 0) current = el.id;
        else break;
      }
      setActiveId(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [toc, blog?.content]);

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
      setTocOpen(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    } catch { return ""; }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  if (loading) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FAF7F3" }}>
        <p style={{ color: "#725b47", fontFamily: "Lato" }}>Loading...</p>
      </div>
    );
  }

  if (notFound || !blog) {
    return (
      <div data-testid="blog-not-found" style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#FAF7F3", gap: 16 }}>
        <h1 style={{ fontFamily: "Cinzel, serif", color: "#2E221A" }}>Blog Not Found</h1>
        <Link to="/blogs" style={{ color: "#8B4513", fontFamily: "Lato" }}>Back to Blogs</Link>
      </div>
    );
  }

  const excerpt = blog.excerpt || stripHtml(blog.content).substring(0, 160);

  return (
    <div data-testid="blog-detail-page" className="blog-detail-page" style={{ minHeight: "80vh", background: "#FAF7F3", padding: "40px 0" }}>
      <Helmet>
        <title>{blog.title} | Amatya Blog</title>
        <meta name="description" content={excerpt} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://www.amatya.co.in/blogs/${blog.slug}`} />
        <meta property="og:title" content={`${blog.title} | Amatya Blog`} />
        <meta property="og:description" content={excerpt} />
        <meta property="og:url" content={`https://www.amatya.co.in/blogs/${blog.slug}`} />
        {blog.featured_image && <meta property="og:image" content={blog.featured_image} />}
      </Helmet>

      <div className="blog-detail-layout">
        <article className="blog-detail-article">
          <Link to="/blogs" data-testid="back-to-blogs" style={{
            display: "inline-block", marginBottom: 24, color: "#8B4513",
            fontFamily: "Lato, sans-serif", fontSize: "0.9rem", textDecoration: "none"
          }}>
            &#8592; Back to Blogs
          </Link>

          <h1 data-testid="blog-title" style={{
            fontFamily: "Cinzel, serif", fontSize: "1.8rem", color: "#2E221A",
            lineHeight: 1.3, marginBottom: 12
          }}>
            {blog.title}
          </h1>

          <div style={{
            display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap",
            fontFamily: "Lato, sans-serif", fontSize: "0.85rem", color: "#9a8572", alignItems: "center"
          }}>
            <span>{formatDate(blog.published_at)}</span>
            {blog.author && <span>By {blog.author}</span>}
            <span data-testid="blog-reading-time" style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "3px 10px", background: "#f5efe8", borderRadius: 12,
              color: "#8B4513", fontWeight: 500
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {readingTime} min read
            </span>
          </div>

          {blog.featured_image && (
            <img
              src={blog.featured_image}
              alt={blog.title}
              data-testid="blog-featured-image"
              style={{
                width: "100%", maxHeight: 400, objectFit: "cover",
                borderRadius: 10, marginBottom: 28
              }}
            />
          )}

          {blog.tags && blog.tags.length > 0 && (
            <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
              {blog.tags.map((tag, i) => (
                <span key={i} style={{
                  padding: "4px 12px", background: "#f5efe8", borderRadius: 14,
                  fontSize: "0.8rem", color: "#8B4513", fontFamily: "Lato, sans-serif"
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Mobile TOC */}
          {toc.length > 0 && (
            <div className="blog-toc-mobile" data-testid="blog-toc-mobile">
              <button
                type="button"
                className="blog-toc-mobile-btn"
                onClick={() => setTocOpen((o) => !o)}
                aria-expanded={tocOpen}
                data-testid="blog-toc-toggle"
              >
                <span>Table of Contents</span>
                <span className={`blog-toc-chevron ${tocOpen ? "open" : ""}`}>▾</span>
              </button>
              {tocOpen && (
                <ul className="blog-toc-list">
                  {toc.map((t) => (
                    <li key={t.id} className={`blog-toc-${t.level} ${activeId === t.id ? "active" : ""}`}>
                      <a href={`#${t.id}`} onClick={(e) => handleTocClick(e, t.id)}>{t.text}</a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div
            ref={contentRef}
            data-testid="blog-content"
            className="blog-content-body"
            dangerouslySetInnerHTML={{ __html: processedHtml }}
          />

          {/* Call-to-Action: WhatsApp */}
          <a
            href={whatsappCtaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="blog-cta-whatsapp"
            data-testid="blog-cta-whatsapp"
            aria-label="Call now on WhatsApp to get your kundli's detailed analysis"
          >
            <span className="blog-cta-text">Call now to get your kundli's detailed Analysis</span>
            <span className="blog-cta-icon" aria-hidden="true">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg>
            </span>
          </a>

          {/* Share buttons */}
          <div className="blog-share" data-testid="blog-share">
            <span className="blog-share-label">Share this post</span>
            <div className="blog-share-buttons">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${blog.title} — https://www.amatya.co.in/blogs/${blog.slug}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="blog-share-btn blog-share-whatsapp"
                aria-label="Share on WhatsApp"
                data-testid="blog-share-whatsapp"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg>
                <span>WhatsApp</span>
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(`https://www.amatya.co.in/blogs/${blog.slug}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="blog-share-btn blog-share-x"
                aria-label="Share on X"
                data-testid="blog-share-x"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                <span>X</span>
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://www.amatya.co.in/blogs/${blog.slug}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="blog-share-btn blog-share-facebook"
                aria-label="Share on Facebook"
                data-testid="blog-share-facebook"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <span>Facebook</span>
              </a>
              <button
                type="button"
                onClick={() => {
                  const url = `https://www.amatya.co.in/blogs/${blog.slug}`;
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(url).then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    });
                  }
                }}
                className="blog-share-btn blog-share-copy"
                aria-label="Copy link"
                data-testid="blog-share-copy"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                <span>{copied ? "Copied!" : "Copy link"}</span>
              </button>
            </div>
          </div>

          {/* Related Posts */}
          {related.length > 0 && (
            <section className="blog-related" data-testid="blog-related">
              <h3 className="blog-related-title">Related Reading</h3>
              <div className="blog-related-grid">
                {related.map((r) => (
                  <Link
                    key={r.id || r.slug}
                    to={`/blogs/${r.slug}`}
                    className="blog-related-card"
                    data-testid={`related-${r.slug}`}
                  >
                    {r.featured_image && (
                      <div
                        className="blog-related-img"
                        style={{ backgroundImage: `url(${r.featured_image})` }}
                        aria-hidden="true"
                      />
                    )}
                    <div className="blog-related-body">
                      <div className="blog-related-card-title">{r.title}</div>
                      <div className="blog-related-card-meta">{formatDate(r.published_at)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        {/* Desktop sticky TOC */}
        {toc.length > 0 && (
          <aside className="blog-toc-desktop" data-testid="blog-toc-desktop" aria-label="Table of contents">
            <div className="blog-toc-sticky">
              <div className="blog-toc-title">On this page</div>
              <ul className="blog-toc-list">
                {toc.map((t) => (
                  <li key={t.id} className={`blog-toc-${t.level} ${activeId === t.id ? "active" : ""}`}>
                    <a href={`#${t.id}`} onClick={(e) => handleTocClick(e, t.id)}>{t.text}</a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default BlogDetailPage;
